import React, { useId } from 'react';
import { clsx } from 'clsx';

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  /** Label text above the textarea */
  label?: string;
  /** Helper/description text below the label */
  description?: string;
  /** Error message (overrides description) */
  error?: string;
  /** Control resize behavior */
  resize?: 'none' | 'vertical' | 'horizontal' | 'both';
  /** Full width */
  fullWidth?: boolean;
}

/**
 * Shared Textarea component with consistent styling, label, description, and error states.
 *
 * Uses CSS variables for theme-aware rendering. Focus ring uses purple accent.
 *
 * @example
 * ```tsx
 * <Textarea
 *   label="Descrição"
 *   description="Descreva o item em detalhes"
 *   value={desc}
 *   onChange={(e) => setDesc(e.target.value)}
 *   rows={4}
 *   error={errors.desc}
 * />
 * ```
 */
export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      label,
      description,
      error,
      resize = 'vertical',
      fullWidth = true,
      className,
      style,
      id: propId,
      ...props
    },
    ref
  ) => {
    const generatedId = useId();
    const inputId = propId || generatedId;
    const hasError = !!error;

    const resizeMap = {
      none: 'none',
      vertical: 'vertical',
      horizontal: 'horizontal',
      both: 'both',
    } as const;

    return (
      <div className={clsx({ 'w-full': fullWidth })}>
        {label && (
          <label
            htmlFor={inputId}
            className="block text-[13px] font-semibold mb-1.5"
            style={{ color: 'var(--theme-text)' }}
          >
            {label}
            {props.required && (
              <span className="ml-1" style={{ color: 'var(--theme-error-color)' }}>*</span>
            )}
          </label>
        )}
        {description && !error && (
          <p className="text-xs mb-1.5" style={{ color: 'var(--theme-muted-text)' }}>
            {description}
          </p>
        )}

        <textarea
          ref={ref}
          id={inputId}
          className={clsx(
            'w-full min-h-[62px] rounded-[9px] border px-[11px] py-2 text-sm',
            'placeholder:opacity-60',
            'focus-visible:outline-none',
            'disabled:cursor-not-allowed disabled:opacity-50',
            className
          )}
          style={{
            backgroundColor: 'var(--theme-input-background)',
            borderColor: hasError ? 'var(--theme-error-color)' : 'var(--theme-input-border)',
            color: 'var(--theme-input-text)',
            resize: resizeMap[resize],
            transition: 'border-color 150ms, box-shadow 150ms',
            ...style,
          }}
          onFocus={(e) => {
            if (!hasError && !props.disabled) {
              e.currentTarget.style.borderColor = 'var(--theme-input-focus)';
              e.currentTarget.style.boxShadow = '0 0 0 3px var(--ring-color)';
            }
            props.onFocus?.(e);
          }}
          onBlur={(e) => {
            e.currentTarget.style.borderColor = hasError
              ? 'var(--theme-error-color)'
              : 'var(--theme-input-border)';
            e.currentTarget.style.boxShadow = 'none';
            props.onBlur?.(e);
          }}
          {...props}
        />

        {error && (
          <p className="text-xs mt-1" style={{ color: 'var(--theme-error-color)' }} role="alert">
            {error}
          </p>
        )}
      </div>
    );
  }
);

Textarea.displayName = 'Textarea';
